<template>
  <div class="menu-stat-v3" v-loading="loading">
    <div class="stat-section-header">
      <div class="header-title-group">
        <div class="header-icon-badge"><i class="el-icon-dish"></i></div>
        <div>
          <h2 class="header-title">Thống Kê Thực Đơn & Tiến Độ Đăng Khẩu Phần Ăn</h2>
          <p class="header-subtitle">Theo dõi tiến độ hoàn thành thực đơn tuần của các lớp</p>
        </div>
      </div>
      <div class="header-badge-group">
        <span class="live-indicator"><span class="pulse-dot"></span>
          Đã đăng: {{ hasData ? report.publishedClasses : "—" }}/{{ hasData ? report.totalClasses : "—" }} lớp
          ({{ hasData ? report.publishRate.toFixed(1) : "—" }}%)
        </span>
      </div>
    </div>

    <div class="filter-control-bar">
      <div class="filter-left">
        <div class="filter-chip">
          <span class="chip-label"><i class="el-icon-date"></i> Chọn tuần:</span>
          <el-date-picker v-model="selectedWeekDate" type="week" size="small"
                          format="Tuần WW (yyyy)" placeholder="Chọn tuần xem"
                          :clearable="false" class="week-picker" @change="loadReport" />
        </div>
        <div class="week-nav-btn-group">
          <button class="nav-arrow-btn" title="Tuần trước" @click="changeWeek(-1)"><i class="el-icon-arrow-left"></i></button>
          <button class="nav-current-btn" @click="currentWeek">Tuần này</button>
          <button class="nav-arrow-btn" title="Tuần sau" @click="changeWeek(1)"><i class="el-icon-arrow-right"></i></button>
        </div>
        <div class="week-range-tag"><i class="el-icon-calendar"></i><span>{{ weekRangeLabel }}</span></div>
        <div class="filter-chip">
          <span class="chip-label"><i class="el-icon-collection-tag"></i> Khối:</span>
          <el-select v-model="selectedGrade" size="small" class="grade-select" @change="loadReport">
            <el-option label="Tất cả các khối" value="" />
            <el-option v-for="grade in gradeOptions" :key="grade.gradeId"
                       :label="grade.gradeName" :value="String(grade.gradeId)" />
          </el-select>
        </div>
      </div>
    </div>

    <el-alert v-if="errorMessage" :title="errorMessage" type="error" :closable="false" show-icon />
    <el-alert v-if="!loading && !errorMessage && !hasData" title="Chưa có dữ liệu thống kê thực đơn"
              type="info" :closable="false" show-icon />

    <template v-if="hasData">
      <p v-if="isHistoricalWeek" class="roster-note">Số lớp của tuần cũ được đối chiếu theo danh sách lớp hiện tại.</p>
      <div class="menu-kpi-grid">
        <div class="menu-kpi-card border-indigo">
          <div class="kpi-icon-wrap bg-indigo-soft text-indigo"><i class="el-icon-s-home"></i></div>
          <div class="kpi-details">
            <span class="kpi-name">Tổng số lớp học</span>
            <div class="kpi-number text-indigo">{{ report.totalClasses }} <span class="kpi-unit">lớp</span></div>
            <div class="kpi-hint">{{ selectedGradeLabel }}</div>
          </div>
        </div>
        <div class="menu-kpi-card border-emerald interactive-card" @click="openMenuDialog('published')">
          <div class="kpi-icon-wrap bg-emerald-soft text-emerald"><i class="el-icon-circle-check"></i></div>
          <div class="kpi-details">
            <div class="kpi-title-row"><span class="kpi-name">Số lớp đã đăng</span>
              <span class="click-hint-badge text-emerald">Xem chi tiết <i class="el-icon-arrow-right"></i></span></div>
            <div class="kpi-number text-emerald">{{ report.publishedClasses }} <span class="kpi-unit">lớp</span></div>
            <div class="kpi-hint text-emerald"><i class="el-icon-check"></i> Đạt {{ report.publishRate.toFixed(1) }}%</div>
          </div>
        </div>
        <div class="menu-kpi-card border-rose interactive-card" @click="openMenuDialog('unpublished')">
          <div class="kpi-icon-wrap bg-rose-soft text-rose"><i class="el-icon-warning-outline"></i></div>
          <div class="kpi-details">
            <div class="kpi-title-row"><span class="kpi-name">Số lớp chưa đăng</span>
              <span class="click-hint-badge text-rose">Xem chi tiết <i class="el-icon-arrow-right"></i></span></div>
            <div class="kpi-number text-rose">{{ report.unpublishedClasses }} <span class="kpi-unit">lớp</span></div>
            <div class="kpi-hint text-rose"><i class="el-icon-bell"></i> Còn {{ report.unpublishedRate.toFixed(1) }}% cần nộp</div>
          </div>
        </div>
      </div>

      <el-row :gutter="20" class="charts-row">
        <el-col :xs="24" :sm="24" :md="14" :lg="15">
          <div class="chart-card h-100">
            <div class="chart-card-head">
              <div><h3 class="chart-title">Tình Hình Đăng Thực Đơn Theo Khối</h3>
                <p class="chart-subtitle">{{ weekRangeLabel }} • So sánh số lớp đã đăng và chưa đăng theo khối</p></div>
              <div class="chart-legend-chips">
                <span class="legend-dot-item"><span class="chip-color chip-pub"></span> Đã đăng</span>
                <span class="legend-dot-item"><span class="chip-color chip-unpub"></span> Chưa đăng</span>
              </div>
            </div>
            <div v-if="gradeStats.length" class="grade-chart-scroll">
              <div class="grade-chart" ref="chartGradeMenu" :style="{ width: gradeChartWidth }"></div>
            </div>
            <p v-else class="grade-chart-empty">Không có lớp trong phạm vi đang chọn.</p>
          </div>
        </el-col>
        <el-col :xs="24" :sm="24" :md="10" :lg="9">
          <div class="chart-card h-100 gauge-container-card">
            <div class="chart-card-head">
              <div><h3 class="chart-title">Tỷ Lệ Hoàn Thành {{ selectedGrade ? selectedGradeLabel : 'Toàn Trường' }}</h3>
                <p class="chart-subtitle">Tiến độ cập nhật thực đơn tuần</p></div>
              <span class="gauge-status-badge" :class="report.publishRate >= 80 ? 'badge-good' : 'badge-warn'">
                {{ report.publishRate >= 80 ? 'Tiến độ tốt' : 'Cần đôn đốc' }}
              </span>
            </div>
            <div class="gauge-chart-box" ref="chartGaugeMenu"></div>
            <div class="gauge-summary-footer">
              <div class="gauge-summary-stat"><span class="stat-lbl">Đã hoàn thành</span>
                <strong class="stat-val text-emerald">{{ report.publishedClasses }} lớp</strong></div>
              <div class="gauge-divider"></div>
              <div class="gauge-summary-stat"><span class="stat-lbl">Còn thiếu</span>
                <strong class="stat-val text-rose">{{ report.unpublishedClasses }} lớp</strong></div>
            </div>
            <div class="quick-action-strip">
              <el-button type="primary" size="mini" plain icon="el-icon-circle-check" @click="openMenuDialog('published')">
                Danh sách đã đăng ({{ report.publishedClasses }})
              </el-button>
              <el-button type="danger" size="mini" plain icon="el-icon-warning" @click="openMenuDialog('unpublished')">
                Danh sách chưa đăng ({{ report.unpublishedClasses }})
              </el-button>
            </div>
          </div>
        </el-col>
      </el-row>

      <el-dialog :visible.sync="dialogVisible" width="60%" top="7vh" custom-class="modern-menu-dialog" :destroy-on-close="true">
        <div slot="title" class="dialog-custom-header">
          <div class="header-icon-box" :class="activeTab === 'published' ? 'box-emerald' : 'box-rose'">
            <i :class="activeTab === 'published' ? 'el-icon-circle-check' : 'el-icon-warning-outline'"></i>
          </div>
          <div><h3 class="dialog-heading">{{ activeTab === 'published' ? 'Danh Sách Các Lớp Đã Đăng Thực Đơn' : 'Danh Sách Các Lớp Chưa Đăng Thực Đơn' }}</h3>
            <p class="dialog-subheading">{{ weekRangeLabel }} • {{ selectedGradeLabel }}</p></div>
        </div>
        <div class="dialog-tab-bar">
          <button :class="['dialog-tab-btn', { active: activeTab === 'published' }]" @click="activeTab = 'published'">
            Lớp đã đăng ({{ publishedList.length }})
          </button>
          <button :class="['dialog-tab-btn', 'btn-danger-tab', { active: activeTab === 'unpublished' }]" @click="activeTab = 'unpublished'">
            Lớp chưa đăng ({{ unpublishedList.length }})
          </button>
          <div class="dialog-search-wrap"><el-input v-model="searchQuery" size="small"
            placeholder="Tìm theo tên lớp hoặc khối" prefix-icon="el-icon-search" clearable class="dialog-search-input" /></div>
        </div>
        <div class="dialog-table-container">
          <el-table :data="filteredDialogList" stripe border size="small" class="modern-menu-table"
                    empty-text="Không có lớp trong danh sách">
            <el-table-column type="index" label="STT" width="55" align="center" />
            <el-table-column label="Tên lớp" min-width="170">
              <template slot-scope="scope"><div class="class-title-cell">
                <strong class="text-indigo">{{ scope.row.className }}</strong>
                <span class="sub-grade">{{ scope.row.gradeName }}</span>
              </div></template>
            </el-table-column>
            <el-table-column label="Kiểu thực đơn" min-width="135">
              <template slot-scope="scope">{{ submissionTypeLabel(scope.row.submissionType) }}</template>
            </el-table-column>
            <el-table-column label="Ngày còn thiếu" min-width="180">
              <template slot-scope="scope">{{ missingDatesLabel(scope.row.missingDates) }}</template>
            </el-table-column>
            <el-table-column label="Cập nhật gần nhất" min-width="150" align="center">
              <template slot-scope="scope">{{ formatDateTime(scope.row.updatedAt) }}</template>
            </el-table-column>
          </el-table>
        </div>
        <div slot="footer" class="dialog-footer"><el-button size="small" @click="dialogVisible = false">Đóng</el-button></div>
      </el-dialog>
    </template>
  </div>
</template>

<script>
import * as echarts from "echarts";
import moment from "moment";
import MenuWeeklyStatisticsService from "@/services/dashboard_v3/MenuWeeklyStatisticsService";

export default {
  name: "MenuStatisticsV3",
  props: {
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hasLoadedData: false,
      isLoadingData: false,
      selectedWeekDate: moment().toDate(),
      selectedGrade: "",
      gradeOptions: [],
      report: null,
      loading: false,
      errorMessage: "",
      requestSequence: 0,
      dialogVisible: false,
      activeTab: "published",
      searchQuery: "",
      chartGrade: null,
      chartGauge: null
    };
  },
  computed: {
    hasData() { return !!this.report; },
    weekRangeLabel() {
      const week = moment(this.selectedWeekDate);
      return `Tuần ${week.isoWeek()} (${week.clone().startOf("isoWeek").format("DD/MM/YYYY")} - ${week.clone().endOf("isoWeek").format("DD/MM/YYYY")})`;
    },
    selectedGradeLabel() {
      if (!this.selectedGrade) return "Toàn bộ trường";
      const grade = this.gradeOptions.find(item => String(item.gradeId) === this.selectedGrade);
      return grade ? grade.gradeName : "Khối đã chọn";
    },
    isHistoricalWeek() {
      return this.hasData && moment(this.report.weekEndDate).isBefore(moment().startOf("isoWeek"), "day");
    },
    gradeStats() { return this.hasData ? this.report.gradeStats : []; },
    gradeChartWidth() { return `${Math.max(480, this.gradeStats.length * 112)}px`; },
    publishedList() { return this.hasData ? this.report.classes.filter(item => item.published) : []; },
    unpublishedList() { return this.hasData ? this.report.classes.filter(item => !item.published) : []; },
    filteredDialogList() {
      const items = this.activeTab === "published" ? this.publishedList : this.unpublishedList;
      const query = this.searchQuery.trim().toLocaleLowerCase("vi");
      if (!query) return items;
      return items.filter(item => `${item.className} ${item.gradeName}`.toLocaleLowerCase("vi").includes(query));
    }
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    if (this.autoLoad) {
      this.loadData();
    }
  },
  beforeDestroy() {
    this.requestSequence += 1;
    window.removeEventListener("resize", this.handleResize);
    this.disposeCharts();
  },
  methods: {
    async loadData() {
      if (this.hasLoadedData || this.isLoadingData) return;
      this.isLoadingData = true;
      try {
        await this.loadReport();
        this.hasLoadedData = true;
      } finally {
        this.isLoadingData = false;
      }
    },
    changeWeek(delta) {
      this.selectedWeekDate = moment(this.selectedWeekDate).add(delta, "weeks").toDate();
      this.loadReport();
    },
    currentWeek() {
      this.selectedWeekDate = moment().toDate();
      this.loadReport();
    },
    loadReport() {
      if (!this.selectedWeekDate) return;
      const requestId = ++this.requestSequence;
      const weekStartDate = moment(this.selectedWeekDate).startOf("isoWeek").format("YYYY-MM-DD");
      const gradeId = this.selectedGrade ? Number(this.selectedGrade) : undefined;
      this.loading = true;
      this.errorMessage = "";
      return MenuWeeklyStatisticsService.getWeeklyProgress(weekStartDate, gradeId)
        .then(response => {
          if (requestId !== this.requestSequence) return;
          const data = response && response.data && response.data.data;
          if (!data || !Array.isArray(data.classes) || !Array.isArray(data.gradeStats)) {
            throw new Error("Dữ liệu thống kê thực đơn không hợp lệ");
          }
          this.report = data;
          this.gradeOptions = Array.isArray(data.gradeOptions) ? data.gradeOptions : [];
          this.$nextTick(this.renderCharts);
        })
        .catch(error => {
          if (requestId !== this.requestSequence) return;
          this.report = null;
          this.dialogVisible = false;
          this.disposeCharts();
          this.errorMessage = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message : (error.message || "Không tải được thống kê thực đơn");
        })
        .finally(() => { if (requestId === this.requestSequence) this.loading = false; });
    },
    openMenuDialog(tabName) {
      this.activeTab = tabName;
      this.searchQuery = "";
      this.dialogVisible = true;
    },
    submissionTypeLabel(value) {
      return { DAILY: "Theo ngày", FILE: "Tệp/ảnh", BOTH: "Theo ngày và tệp/ảnh", NONE: "Chưa đủ" }[value] || "—";
    },
    missingDatesLabel(dates) {
      return dates && dates.length ? dates.map(date => moment(date).format("DD/MM")).join(", ") : "—";
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "—";
    },
    handleResize() {
      if (this.chartGrade) this.chartGrade.resize();
      if (this.chartGauge) this.chartGauge.resize();
    },
    disposeCharts() {
      if (this.chartGrade) { this.chartGrade.dispose(); this.chartGrade = null; }
      if (this.chartGauge) { this.chartGauge.dispose(); this.chartGauge = null; }
    },
    renderCharts() {
      if (!this.hasData) return;
      const gradeDom = this.$refs.chartGradeMenu;
      const gaugeDom = this.$refs.chartGaugeMenu;
      if (!gaugeDom) return;
      if (gradeDom) {
        if (!this.chartGrade) this.chartGrade = echarts.init(gradeDom);
        this.chartGrade.resize();
        const grades = this.gradeStats;
        this.chartGrade.setOption({
          tooltip: {
            trigger: "axis", axisPointer: { type: "shadow" }, appendToBody: true,
            formatter: params => {
              const grade = params.length ? grades[params[0].dataIndex] : null;
              if (!grade) return "";
              const name = echarts.format.encodeHTML(String(grade.gradeName || ""));
              return `<b>${name}</b><br/>Đã đăng: ${grade.publishedClasses} lớp<br/>Chưa đăng: ${grade.unpublishedClasses} lớp<br/>Tỷ lệ đã đăng: ${Number(grade.publishRate).toFixed(1)}%`;
            }
          },
          grid: { top: 35, left: 12, right: 20, bottom: 14, containLabel: true },
          xAxis: {
            type: "category", data: grades.map(grade => grade.gradeName),
            axisLine: { lineStyle: { color: "#cbd5e1" } }, axisTick: { show: false },
            axisLabel: { interval: 0, width: 90, overflow: "truncate", color: "#475569", fontWeight: 600, fontSize: 12,
              formatter: name => name.length > 14 ? `${name.slice(0, 11)}...` : name }
          },
          yAxis: {
            type: "value", name: "Số lớp", minInterval: 1,
            nameTextStyle: { color: "#94a3b8", fontSize: 11 },
            splitLine: { lineStyle: { color: "#f1f5f9" } },
            axisLabel: { color: "#64748b" }
          },
          series: [
            { name: "Đã đăng", type: "bar", barWidth: 22, barGap: "20%",
              itemStyle: { color: "#10b981", borderRadius: [6, 6, 0, 0] },
              data: grades.map(grade => grade.publishedClasses) },
            { name: "Chưa đăng", type: "bar", barWidth: 22, barGap: "20%",
              itemStyle: { color: "#f43f5e", borderRadius: [6, 6, 0, 0] },
              data: grades.map(grade => grade.unpublishedClasses) }
          ]
        }, true);
      } else if (this.chartGrade) {
        this.chartGrade.dispose();
        this.chartGrade = null;
      }
      if (!this.chartGauge) this.chartGauge = echarts.init(gaugeDom);
      this.chartGauge.setOption({
        series: [{ type: "gauge", startAngle: 180, endAngle: 0, center: ["50%", "72%"], radius: "105%",
          min: 0, max: 100, splitNumber: 5,
          axisLine: { lineStyle: { width: 14, color: [[0.6, "#f43f5e"], [0.85, "#f59e0b"], [1, "#10b981"]] } },
          pointer: { length: "12%", width: 12, offsetCenter: [0, "-55%"] },
          axisTick: { length: 8, lineStyle: { color: "auto", width: 1.5 } },
          splitLine: { length: 14, lineStyle: { color: "auto", width: 2.5 } },
          axisLabel: { color: "#64748b", fontSize: 11, distance: -38,
            formatter: value => value === 0 || value === 50 || value === 100 ? `${value}%` : "" },
          title: { offsetCenter: [0, "15%"], fontSize: 13, color: "#64748b" },
          detail: { fontSize: 34, offsetCenter: [0, "-15%"], fontWeight: 800, formatter: "{value}%", color: "#0f172a" },
          data: [{ value: this.report.publishRate, name: "Đã có thực đơn tuần" }] }]
      }, true);
    }
  }
};
</script>

<style scoped>
.roster-note { margin: 12px 0; color: #64748b; font-size: 12px; }
.menu-stat-v3 {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px -3px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
}

/* Header */
.stat-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #fef3c7;
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.header-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.header-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #059669;
  background: #ecfdf5;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid #a7f3d0;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
  animation: pulseDot 2s infinite;
}

@keyframes pulseDot {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
}

/* 1. Control Toolbar */
.filter-control-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 10px 16px;
  margin-bottom: 22px;
}

.filter-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-chip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chip-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.week-picker {
  width: 175px;
}

.grade-select {
  width: 185px;
}

.week-nav-btn-group {
  display: inline-flex;
  background: #e2e8f0;
  border-radius: 8px;
  padding: 2px;
}

.nav-arrow-btn {
  border: none;
  background: transparent;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  transition: all 0.15s ease;
}

.nav-arrow-btn:hover {
  background: #ffffff;
  color: #d97706;
}

.nav-current-btn {
  border: none;
  background: transparent;
  padding: 0 10px;
  height: 28px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.nav-current-btn:hover {
  background: #ffffff;
  color: #d97706;
}

.week-range-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #b45309;
  background: #fef3c7;
  padding: 5px 12px;
  border-radius: 8px;
}

/* 2. Cụm 3 KPI Cards: Tổng số lớp, Đã đăng, Chưa đăng */
.menu-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.menu-kpi-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  transition: all 0.2s ease;
}

.interactive-card {
  cursor: pointer;
  position: relative;
}

.interactive-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
}

.border-indigo { border-left: 5px solid #6366f1; }
.border-emerald { border-left: 5px solid #10b981; }
.border-rose { border-left: 5px solid #f43f5e; }

.kpi-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.bg-indigo-soft { background: #ede9fe; }
.text-indigo { color: #6366f1; }
.bg-emerald-soft { background: #dcfce7; }
.text-emerald { color: #10b981; }
.bg-rose-soft { background: #ffe4e6; }
.text-rose { color: #f43f5e; }

.kpi-details {
  flex: 1;
}

.kpi-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.kpi-name {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  display: block;
  margin-bottom: 4px;
}

.click-hint-badge {
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #ffffff;
  padding: 2px 8px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.kpi-number {
  font-size: 26px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 4px;
}

.kpi-unit {
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
}

.kpi-hint {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

/* 3. Charts Area */
.charts-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.charts-row::before,
.charts-row::after {
  display: none;
}

.charts-row > .el-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.chart-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  box-sizing: border-box;
}

.chart-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.chart-title {
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.chart-subtitle {
  margin: 0;
  font-size: 12px;
  color: #64748b;
}

.chart-legend-chips {
  display: flex;
  gap: 12px;
  align-items: center;
}

.legend-dot-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.chip-color {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

.chip-pub { background: #10b981; }
.chip-unpub { background: #f43f5e; }

.grade-chart-scroll {
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.grade-chart {
  min-width: 100%;
  height: 310px;
}

.grade-chart-empty {
  margin: 40px 0;
  color: #94a3b8;
  text-align: center;
  font-size: 13px;
}

.gauge-container-card {
  display: flex;
  flex-direction: column;
}

.gauge-status-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
}

.badge-good {
  background: #dcfce7;
  color: #15803d;
}

.badge-warn {
  background: #fee2e2;
  color: #b91c1c;
}

.gauge-chart-box {
  height: 200px;
  width: 100%;
}

.gauge-summary-footer {
  display: flex;
  justify-content: space-around;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 14px;
  margin: auto 0 14px;
}

.gauge-summary-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stat-lbl {
  font-size: 11.5px;
  color: #64748b;
  font-weight: 500;
}

.stat-val {
  font-size: 16px;
  font-weight: 800;
}

.gauge-divider {
  width: 1px;
  height: 28px;
  background: #e2e8f0;
}

.quick-action-strip {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}


/* 5. Dialog Styles */
.dialog-custom-header {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.box-emerald {
  background: #dcfce7;
  color: #10b981;
}

.box-rose {
  background: #ffe4e6;
  color: #f43f5e;
}

.dialog-heading {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subheading {
  margin: 0;
  font-size: 12.5px;
  color: #64748b;
}

.dialog-tab-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 12px;
  flex-wrap: wrap;
}

.dialog-tab-btn {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.dialog-tab-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.dialog-tab-btn.active {
  background: #10b981;
  color: #ffffff;
  border-color: #10b981;
}

.btn-danger-tab.active {
  background: #f43f5e;
  border-color: #f43f5e;
  color: #ffffff;
}

.dialog-search-wrap {
  margin-left: auto;
  width: 250px;
}

.dialog-table-container {
  max-height: 460px;
  overflow-y: auto;
}

.class-title-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sub-grade {
  font-size: 11.5px;
  color: #64748b;
}

.time-stamp {
  font-size: 12px;
  color: #475569;
}

.reminder-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.reminder-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: #9f1239;
}

/* Responsive */
@media (max-width: 992px) {
  .menu-kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .filter-control-bar {
    flex-direction: column;
    align-items: flex-start;
  }
  .menu-kpi-grid {
    grid-template-columns: 1fr;
  }
  .chart-card-head {
    flex-wrap: wrap;
  }
}
</style>
